import { IsoDateTime, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { Actor, Bindings, Identity, Key, Ref, Scope } from "./primitives.ts";
import { CronExpression, TimeZone } from "./schedule.ts";

export const TriggerKind = z.enum(["MANUAL", "SCHEDULE", "WEBHOOK", "API_CALL", "EVENT", "DATA_CHANGE", "SUBFLOW", "SYSTEM"]);
const base = { ...Identity, inputContractRef: Ref, inputMapping: Bindings, policyRef: Ref };
export const TriggerDefinitionVersion = z.discriminatedUnion("type", [
  z.object({ ...base, type: z.literal("MANUAL") }).strict(),
  z.object({ ...base, type: z.literal("SCHEDULE"), schedule: z.object({ expression: CronExpression, timeZone: TimeZone,
    misfire: z.enum(["SKIP", "CATCH_UP_BOUNDED"]), maxCatchUp: z.number().int().min(0).max(100), overlap: z.enum(["SKIP", "QUEUE", "ALLOW_BOUNDED"]),
    concurrency: z.number().int().min(1).max(100), dstGap: z.enum(["SKIP", "NEXT_VALID"]), dstFold: z.enum(["FIRST", "SECOND"]),
  }).strict().superRefine((v, ctx) => {
    if ((v.misfire === "SKIP") !== (v.maxCatchUp === 0)) ctx.addIssue({ code: "custom", path: ["maxCatchUp"], message: "INVALID_CATCH_UP_BOUND" });
    if (v.overlap !== "ALLOW_BOUNDED" && v.concurrency !== 1) ctx.addIssue({ code: "custom", path: ["concurrency"], message: "INVALID_OVERLAP_BOUND" });
  }) }).strict(),
  z.object({ ...base, type: z.literal("WEBHOOK"), sourceProfileRef: Ref, signaturePolicyRef: Ref }).strict(),
  z.object({ ...base, type: z.literal("API_CALL"), sourceProfileRef: Ref }).strict(),
  z.object({ ...base, type: z.literal("EVENT"), sourceProfileRef: Ref, eventType: Key }).strict(),
  z.object({ ...base, type: z.literal("DATA_CHANGE"), sourceProfileRef: Ref, collectionRef: Ref, change: z.enum(["CREATED", "UPDATED", "RETIRED"]) }).strict(),
  z.object({ ...base, type: z.literal("SUBFLOW"), parentFlowRef: Ref, childPolicyRef: Ref }).strict(),
  z.object({ ...base, type: z.literal("SYSTEM"), systemPolicyRef: Ref }).strict(),
]);
export const TriggerBindingState = z.enum(["DRAFT", "ACTIVE", "PAUSED", "DISABLED", "RETIRED"]);
export const TriggerBinding = z.object({ id: Uuid, scope: Scope, revision: z.number().int().positive(), definitionRef: Ref, flowRef: Ref,
  state: TriggerBindingState, principal: Actor, grantRef: Ref, effectiveAt: IsoDateTime, backlogPolicy: z.enum(["SKIP", "CATCH_UP_BOUNDED"]), maxBacklog: z.number().int().min(0).max(100),
}).strict().superRefine((v, ctx) => {
  if ((v.backlogPolicy === "SKIP") !== (v.maxBacklog === 0)) ctx.addIssue({ code: "custom", path: ["maxBacklog"], message: "INVALID_BACKLOG_BOUND" });
});
export const TriggerEvent = z.object({ id: Uuid, workspaceId: Uuid, bindingId: Uuid, sourceIdentity: Key, occurrenceKey: z.string().min(1).max(512),
  occurredAt: IsoDateTime, receivedAt: IsoDateTime, payloadArtifactRef: Ref,
}).strict();
const receipt = { eventId: Uuid, workspaceId: Uuid, bindingId: Uuid, occurrenceKey: z.string().min(1).max(512) };
export const TriggerReceipt = z.discriminatedUnion("status", [
  z.object({ ...receipt, status: z.literal("ACCEPTED"), runId: Uuid, definitionRef: Ref, flowRef: Ref }).strict(),
  z.object({ ...receipt, status: z.literal("DUPLICATE"), runId: Uuid, originalReceiptId: Uuid }).strict(),
  z.object({ ...receipt, status: z.literal("REJECTED"), reason: Key }).strict(),
  z.object({ ...receipt, status: z.literal("SKIPPED"), reason: Key }).strict(),
]);

// Chỉ là khoá contract; admission vẫn cần verified source, current grant và transaction unique constraint ở E.2.
// Không đưa flowVersion/bindingRevision vào key: replay sau upgrade vẫn nhận logical Run cũ.
export function triggerOccurrenceIdentity(event: z.infer<typeof TriggerEvent>): string {
  return JSON.stringify([event.workspaceId, event.bindingId, event.sourceIdentity, event.occurrenceKey]);
}
export function canTransitionTrigger(from: z.infer<typeof TriggerBindingState>, to: z.infer<typeof TriggerBindingState>): boolean {
  const edges: Record<z.infer<typeof TriggerBindingState>, readonly z.infer<typeof TriggerBindingState>[]> = {
    DRAFT: ["ACTIVE", "DISABLED", "RETIRED"], ACTIVE: ["PAUSED", "DISABLED", "RETIRED"],
    PAUSED: ["ACTIVE", "DISABLED", "RETIRED"], DISABLED: ["DRAFT", "RETIRED"], RETIRED: [],
  };
  return edges[from].includes(to);
}
